/**
 * CreatorSection — creator-hub content area.
 *
 * Architecture (core requirement: browsing social must not interrupt the stream):
 *
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │ STREAM PANEL — ALWAYS rendered, persistent across tab switches │
 *   │   · Stream embed (auto-source: live > most-recent VOD hero)    │
 *   │   · Source toggle (Twitch / Kick / YouTube)                    │
 *   │   · Live activity ticker                                       │
 *   └────────────────────────────────────────────────────────────────┘
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │ Tab nav: CHALLENGES · CLIPS · SOCIAL                           │
 *   │ (no STREAM tab — the player is always visible above)           │
 *   └────────────────────────────────────────────────────────────────┘
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │ Active tab content                                             │
 *   │   CHALLENGES — brand activations / placeholder when none       │
 *   │   CLIPS      — highlights + clips + short-form across platforms │
 *   │   SOCIAL     — masonry mosaic of X / TikTok / Instagram        │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Switching between CHALLENGES / CLIPS / SOCIAL re-renders only the lower
 * content area. The stream player above stays mounted — no playback
 * restart. This is the core "stream keeps playing while you browse"
 * requirement.
 *
 * Theming: none of its own. The accent comes from `--hub-primary` /
 * `--hub-secondary`, published by the ThemeProvider this component always
 * renders inside, which `CreatorSection.scss` aliases to the `--hcs-*` names
 * its rules paint from. This component read `themeColors` and set those names
 * inline for a while — a second copy of a value already on its own ancestor.
 */

import React, { useEffect, useMemo, useState } from "react";
import type { StreamingSettings, FeaturedStreamResponse } from "@/hooks/hub/useGetHub";
import { StreamPanel } from "@/widgets/StreamPanel";
import {resolveStage, StageOccupantClass, StageState, isNonStreaming} from "@/surfaces/stageOccupant";
import { ChallengesTab } from "@/widgets/ChallengesTab";
import { ClipsTab } from "@/widgets/ClipsTab";
import { SocialMosaic } from "@/widgets/SocialMosaic";
import { ActivityTicker } from "@/widgets/ActivityTicker";
import { HighlightsRow } from "@/widgets/HighlightsRow";
import { CreatorDeliveryRibbon } from "@/widgets/CreatorDeliveryRibbon";
import { SponsorChallengesRow } from "@/widgets/SponsorChallengesRow";
import { CreatorTabsRow } from "./creator-section/CreatorTabsRow";
import { useCreatorPlatforms } from "./creator-section/useCreatorPlatforms";

import { useGetDropLiveStatus } from "@/hooks/drop/useGetDropLiveStatus";
import { asStreamingSource, type ContentTab, type SelectedClip, type StreamingSource } from "@/widgets/shared";
import "./CreatorSection.scss";

interface CreatorSectionProps {
    surfaceId?: string;
    /* Drop slug — set for DropPage consumers; takes precedence over surfaceId for content fetches */
    dropSlug?: string;
    streamingSettings?: StreamingSettings;
    hubName?: string;
    /*
     * Default tab on first render. DropPage decides based on whether the
     * creator has any video-bearing platforms configured — if not, but
     * socials exist, the page opens on SOCIAL so the user sees content
     * immediately rather than an empty Highlights row.
     */
    initialTab?: ContentTab;
    /*
     * True when the creator has at least one challenge surface to show:
     * an attached competition, any quest configs, an active brand
     * activation, or a global / creator-specific challenge. When false
     * the Challenges tab AND its content are both suppressed so the user
     * doesn't land on a permanent "No challenges yet" empty state.
     */
    hasChallenges?: boolean;
    /*
     * Right-anchored slot in the tab nav row. Used by the Drop creator
     * page to surface Share / Invite / More icons next to the tabs (vs
     * stealing their own vertical row). Render-free when omitted.
     */
    tabsActionsSlot?: React.ReactNode;
    /*
     * What holds the Stage when nothing is playing — PAGE's FIRST
     * non-streaming occupant.
     *
     * `PRODUCT_PLAN.md` §2.5b: PAGE has had no non-streaming Stage concept at
     * all. It only ever showed a stream or fell back to a VOD, which is the
     * same Streaming class either way, so the resting state was simply the
     * absence of one — the tabs slid up and nothing took the space.
     *
     * Omitted ⇒ exactly today's behaviour. This is an offer, not a
     * requirement: a creator page with nothing to say while offline should
     * show nothing, not a placeholder.
     */
    idleStageSlot?: React.ReactNode;
    /*
     * Live layout. When true (creator is live), the stream is promoted to the
     * top of the section and the identity strip + tab nav render beneath it:
     * StreamPanel → identitySlot → tabs → body. When false (offline), the
     * original order applies: tabs → StreamPanel → body. Driven by the same
     * page-level live signal that suppresses the full hero above.
     */
    liveLayout?: boolean;
    /*
     * Compact creator-identity strip (avatar / name / followers / socials /
     * follow) rendered directly under the stream in live layout. Supplied by
     * the page (DropHero in identityOnly mode) so all follow/login handlers
     * stay wired. Ignored when liveLayout is false.
     */
    identitySlot?: React.ReactNode;
}

export const CreatorSection: React.FC<CreatorSectionProps> = ({
    surfaceId, dropSlug, streamingSettings, hubName, initialTab, hasChallenges,
    tabsActionsSlot, liveLayout = false, identitySlot, idleStageSlot,
}) => {
    /*
     * Clamp initialTab to a tab that's actually visible — if a caller
     * stored "CHALLENGES" as the preferred tab but the creator has no
     * challenges, fall back to STREAM rather than open a tab that won't
     * be rendered in the nav (which would leave the panel content blank).
     */
    const resolvedInitialTab: ContentTab = initialTab === "CHALLENGES" && !hasChallenges
        ? "STREAM"
        : initialTab ?? "STREAM";
    const [tab, setTab] = useState<ContentTab>(resolvedInitialTab);

    /*
     * Mirror StreamPanel's live-state read so we can move the Active
     * Challenge card down (out of the right rail, into a wide section
     * above SponsorChallengesRow) when the creator is live. React Query
     * dedupes the underlying request — this is the same network call
     * StreamPanel makes, not a second fetch. Source-aware: Drop endpoint
     * for Drop pages, Hub endpoint for Hub pages.
     */
    const dropLiveStatusQuery = useGetDropLiveStatus(dropSlug);
    const liveStatusQuery = dropLiveStatusQuery;
    const liveByPlatform = useMemo(() => {
        const data = (liveStatusQuery.data as { platforms?: Array<{
            platform: string; isLive: boolean; viewerCount: number;
            gameName?: string; streamTitle?: string;
        }> } | null | undefined)?.platforms ?? [];
        const map: Record<string, { isLive: boolean; viewerCount: number }> = {};
        for (const p of data) map[p.platform] = { isLive: p.isLive, viewerCount: p.viewerCount };
        return map;
    }, [liveStatusQuery.data]);

    /*
     * Source-toggle state lifted here so the compact SourceIcons in the tab
     * nav row and the StreamPanel below stay in sync. The two lists and the
     * ordering rule are `useCreatorPlatforms`'.
     */
    const {configured: configuredPlatforms, available: availablePlatforms} =
        useCreatorPlatforms(streamingSettings, liveByPlatform);

    const isLive = useMemo(() => availablePlatforms.some(p => p.isLive), [availablePlatforms]);

    /*
     * ── PAGE'S REFLOW IS A STAGE, RESOLVED BY THE SHARED FUNCTION ──
     *
     * This section has always had a Stage; it just never called it one. The
     * `liveLayout` reflow — stream promoted above the identity strip and the
     * tabs, instead of below them — is the same question HUB's `SurfaceStage`
     * asks: is something playing, and does it own the top of the surface?
     * `SurfaceStage` and this reflow were built independently, for their own
     * good reasons, and nothing ever forced the problem to be solved once.
     *
     * Routing through `resolveStage` is what makes them one mechanic. The
     * MARKUP below is untouched — PAGE keeps its own reflow, exactly as each
     * POV keeps its own roster-card presentation.
     *
     * `liveLayout` stays the input rather than being re-derived from `isLive`
     * here: the page computes it alongside suppressing the full hero above,
     * and deriving it twice is how the section and the hero would come to
     * disagree about the same moment. `isLive` is passed as the class's
     * live-vs-VOD STATE, which is the thing this file does know best.
     */
    const stage = useMemo(() => resolveStage({
        idle: idleStageSlot
            ? {kind: StageOccupantClass.NON_STREAMING, id: "page-idle", widget: idleStageSlot}
            : null,
        streaming: liveLayout
            ? {
                kind: StageOccupantClass.STREAMING,
                id: dropSlug ?? surfaceId ?? "stream",
                /*
                 * A VOD is this class with `live: false` — PAGE's most-recent-VOD
                 * fallback goes through the same player slot, so it is the same
                 * occupant in a different state, never a different mechanic.
                 */
                live: isLive,
                title: hubName ?? "",
            }
            : null,
        /*
         * PAGE does not offer expansion. It has no column to convert into
         * chat — that footprint exists only under HUB — so the request is
         * never made rather than made and refused.
         */
        expandRequested: false,
    }), [liveLayout, isLive, dropSlug, surfaceId, hubName, idleStageSlot]);

    /*
     * The reflow flag, now derived from the shared state rather than read
     * straight off the prop. Identical today by construction; what it buys is
     * that a future change to what "the stage is playing" means reaches PAGE
     * and HUB together instead of one of them.
     */
    const stageIsPlaying = stage.state !== StageState.IDLE;

    /*
     * activeSource starts null on every mount so the default falls through
     * to the precedence-based first-live platform (TWITCH > KICK > YOUTUBE)
     * via the activePlatform memo below. A stored localStorage preference
     * is intentionally NOT loaded — it caused creators who were currently
     * live on Twitch to default to YouTube because a prior visit had
     * persisted YouTube (e.g. Twitch was offline at the time and YouTube
     * was the only available platform). Cross-session "remember my
     * platform" is the wrong default for multi-platform creators; the
     * source-of-truth should be "who is most-live right now." User
     * toggles still work — they update activeSource for the duration of
     * the page session only.
     */
    const [activeSource, setActiveSource] = useState<StreamingSource | null>(null);

    /*
     * Selected-clip state. Lifted here so ClipsTab can write it and StreamPanel
     * can read it. When the creator is OFFLINE and a clip is selected, the
     * main player swaps to that clip. Live streams ALWAYS win — selectedClip
     * is ignored while a stream is live (StreamPanel enforces this).
     */
    const [selectedClip, setSelectedClip] = useState<SelectedClip | null>(null);

    const handleSourceToggle = (platform: StreamingSource) => {
        setActiveSource(platform);
        /* Toggling source clears any explicit clip selection — user wants the live/hero of that platform */
        if (selectedClip) setSelectedClip(null);
    };

    /*
     * Single source of truth for "which platform is active." Falls back to
     * the first live platform when the user hasn't picked one, then to the
     * first configured platform when nothing is live. Both SourceIcons (above
     * tab nav) and StreamPanel (below) read this value so the icon highlight
     * and the player can never disagree mid-render.
     */
    const activePlatform: StreamingSource | null = useMemo(() => {
        if (activeSource && availablePlatforms.some(p => p.platform === activeSource)) return activeSource;
        const firstLive = availablePlatforms.find(p => p.isLive);
        /*
         * NARROWED, not cast. `FeaturedStreamResponse.platform` is an optional
         * four-valued `StreamingPlatform`; this value is handed to SourceIcons
         * and StreamPanel, which only speak the three. `as` here would have
         * pushed a `MULTI` or an `undefined` one hop downstream and made it
         * their crash instead of a null here.
         */
        return asStreamingSource(firstLive?.platform) ??
            asStreamingSource(availablePlatforms[0]?.platform) ??
            null;
    }, [activeSource, availablePlatforms]);

    /*
     * Tab nav row [STREAM · CLIPS · SOCIAL] + source picker + actions. Extracted
     * to a const so the live/offline branches below can reorder it relative to
     * the stream without duplicating the markup.
     */
    const tabsRow =
        <CreatorTabsRow
            tab={tab}
            onTab={setTab}
            hasChallenges={hasChallenges}
            isLive={isLive}
            availablePlatforms={availablePlatforms}
            /*
             * A selected clip means no LIVE source is active — the picker
             * highlights nothing rather than the platform the clip came from.
             */
            activePlatform={selectedClip ? null : activePlatform}
            onSelectSource={handleSourceToggle}
            liveStatusFetchedAt={liveStatusQuery.dataUpdatedAt}
            tabsActionsSlot={tabsActionsSlot}
        />;

    /*
     * Extracted for the same reason as tabsRow — both the live and offline
     * branches reference it. A stable `key` is REQUIRED: when liveLayout flips
     * (creator goes live, or the live-status query resolves), the branch swaps
     * and this element changes sibling position. Without the key React matches
     * children by position+type, sees a type mismatch, and destroys + remounts
     * StreamPanel — restarting the video/iframe, the exact thing promoting the
     * stream is meant to avoid. The key lets React keep the instance mounted.
     */
    const streamPanel =
        <StreamPanel
            key="stream-panel"
            surfaceId={surfaceId}
            dropSlug={dropSlug}
            hubName={hubName}
            streamingSettings={streamingSettings}
            selectedClip={selectedClip}
            onClearSelectedClip={() => setSelectedClip(null)}
            activePlatform={activePlatform}
            setActiveSource={setActiveSource}
            availablePlatforms={availablePlatforms}
        />;
    /*
     * No inline theme style on the root below. `--hcs-primary` /
     * `--hcs-secondary` alias `--hub-*` in the SCSS — see its header. This
     * element renders inside a ThemeProvider publishing those from the same
     * `themeColors`, so the inline copy was a second source for one value.
     */
    return (
        <div className={`creator-section${stageIsPlaying ? " creator-section--live" : ""}`}>
            {/*
              * Live layout: promote the stream to the top, then the relocated
              * creator identity strip, then the tab nav (video → identity → nav).
              * Offline: the original order (nav → video), with the identity living
              * in the full hero above the section.
              */}
            {/*
              * The non-streaming occupant, when the page supplies one. Above
              * the tabs, in the position the stream takes when it is playing —
              * that IS the Stage, and a resting occupant that rendered
              * somewhere else would be a fourth mechanic rather than the same
              * slot holding something different.
              */}
            {!stageIsPlaying && isNonStreaming(stage.occupant) &&
                <div className="creator-section__idle-stage">{stage.occupant.widget}</div>}

            {stageIsPlaying
                ? <>
                    {streamPanel}
                    {identitySlot &&
                        <div className="hcs-identity">{identitySlot}</div>
                    }
                    {tabsRow}
                </>
                : <>
                    {tabsRow}
                    {streamPanel}
                </>
            }

            <div className="hcs-body">
                {/*
                  * STREAM tab — the default landing experience. Renders the
                  * activity feed + highlights + sponsor challenges grid
                  * BELOW the always-rendered player. Other tabs swap this
                  * content out; the player above stays mounted.
                  */}
                {tab === "STREAM" &&
                    <>
                        {/* ActivityTicker suppressed for now — the amber
                            promo strip ("Live highlights drop after each
                            stream" / "Earn XP for watching" / etc.) reads
                            as marketing furniture on a live creator page. */}
                        {/* <ActivityTicker hubName={hubName} /> */}
                        <HighlightsRow
                            dropSlug={dropSlug}
                            hubName={hubName}
                            selectedClipId={selectedClip?.id}
                            onSelectClip={setSelectedClip}
                        />
                        {/*
                          * CreatorDeliveryRibbon — mid-page 7NOW prototype
                          * (between Highlights and Sponsor Challenges).
                          *
                          * Demo-only gate: only renders on develop / staging
                          * deploys so prod doesn't ship a hardcoded 7NOW
                          * sample carousel before the per-Drop partner-
                          * selection backend (V2 scope) is wired. The gate
                          * keys on `VITE_ENVIRONMENT` which CI injects to
                          * "develop" / "staging" / "production"; anything
                          * not strictly "production" still shows the demo,
                          * so local dev + every mirror keeps it visible
                          * for product review.
                          */}
                        {import.meta.env.VITE_ENVIRONMENT !== "production" &&
                            <CreatorDeliveryRibbon partner="7NOW" accountLinked={true} />
                        }
                        {/*
                          * SponsorChallengesRow goes 3-wide when LIVE: the
                          * Sponsor Challenge panel becomes column 1 alongside
                          * the Leaderboard + Bingo cards. When NOT live the
                          * Sponsor Challenge panel renders in StreamPanel's
                          * right rail and this row stays 2-wide as before.
                          */}
                        <SponsorChallengesRow hubName={hubName} isLive={isLive} />
                    </>
                }
                {tab === "CHALLENGES" && hasChallenges && <ChallengesTab hubName={hubName} />}
                {tab === "CLIPS" &&
                    <ClipsTab
                        dropSlug={dropSlug}
                        hubName={hubName}
                        selectedClipId={selectedClip?.id}
                        onSelectClip={setSelectedClip}
                    />
                }
                {tab === "SOCIAL" && <SocialMosaic surfaceId={surfaceId} dropSlug={dropSlug} />}
            </div>
        </div>
    );
};
